// Hosting Manage panel — Loot → Products → a hosting row → "Manage".
//
// Restore points and CDN for a customer's site, without impersonating them
// (impersonation denies backup restore and CDN purge). Same preview → Apply
// pattern as DomainManagePanel; the backend acts as the site's owning login
// only when that login belongs to this account, and records each applied
// change against the signed-in staffer in the account's Events tab.

import { useCallback, useEffect, useState } from 'react';
import { domainAction as productAction } from '../api';
import { readOutcome } from '../lib/domainManage';
import { formatBytes, formatPointDate } from '../lib/hostingManage';

const btn = { padding: '4px 10px', background: 'transparent', color: 'var(--text2)', border: '1px solid var(--border)', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--mono)' };
const primary = { ...btn, background: 'var(--accent)', color: '#fff', border: 'none' };
const label = { fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text3)', fontFamily: 'var(--mono)', marginBottom: 6 };
const section = { padding: '12px 0', borderTop: '1px solid var(--border)' };

export default function HostingManagePanel({ accountId, domain }) {
  const [backups, setBackups] = useState({ loading: true, list: [], error: '' });
  const [cdn, setCdn] = useState({ loading: true, enabled: null, message: '', error: '' });
  const [pending, setPending] = useState(null);   // { name, args, title, message }
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const loadBackups = useCallback(async () => {
    setBackups(b => ({ ...b, loading: true, error: '' }));
    try {
      const out = readOutcome(await productAction(accountId, 'list_my_backups', { domain }));
      setBackups(out.ok
        ? { loading: false, list: out.result?.restorePoints || [], error: '' }
        : { loading: false, list: [], error: out.message });
    } catch (e) { setBackups({ loading: false, list: [], error: e.message }); }
  }, [accountId, domain]);

  const loadCdn = useCallback(async () => {
    setCdn(c => ({ ...c, loading: true, error: '' }));
    try {
      const out = readOutcome(await productAction(accountId, 'get_my_cdn_stats', { domain }));
      setCdn(out.ok
        ? { loading: false, enabled: out.result?.cdnEnabled === true, message: out.result?.message || '', error: '' }
        : { loading: false, enabled: null, message: '', error: out.message });
    } catch (e) { setCdn({ loading: false, enabled: null, message: '', error: e.message }); }
  }, [accountId, domain]);

  useEffect(() => { loadBackups(); loadCdn(); }, [loadBackups, loadCdn]);

  async function preview(name, args, title) {
    setBusy(true); setNotice(null); setPending(null);
    try {
      const out = readOutcome(await productAction(accountId, name, { ...args, domain, confirm: false }, { confirmed: true }));
      if (!out.ok) setNotice({ ok: false, text: out.message });
      else if (out.preview) setPending({ name, args, title, message: out.message });
      else setNotice({ ok: true, text: out.message || 'Nothing to change.' });
    } catch (e) { setNotice({ ok: false, text: e.message }); }
    finally { setBusy(false); }
  }

  async function apply() {
    if (!pending) return;
    setBusy(true);
    try {
      const out = readOutcome(await productAction(accountId, pending.name, { ...pending.args, domain, confirm: true }, { confirmed: true }));
      setNotice(out.ok
        ? { ok: true, text: `${pending.title} — done. Logged to this account's Events.` }
        : { ok: false, text: out.message });
      setPending(null);
      if (out.ok) { loadBackups(); loadCdn(); }
    } catch (e) { setNotice({ ok: false, text: e.message }); }
    finally { setBusy(false); }
  }

  return (
    <div style={{ padding: '4px 0 12px', fontSize: 12 }}>
      {pending && (
        <div role="alertdialog" aria-label={`Confirm ${pending.title}`} style={{ padding: '10px 12px', marginBottom: 10, borderRadius: 6, background: 'rgba(139,92,246,0.08)', border: '1px solid rgba(139,92,246,0.3)' }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{pending.title}</div>
          <div style={{ color: 'var(--text2)', whiteSpace: 'pre-wrap' }}>{pending.message || 'Apply this change?'}</div>
          <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
            <button data-write style={primary} disabled={busy} onClick={apply}>{busy ? 'Applying…' : 'Apply'}</button>
            <button style={btn} disabled={busy} onClick={() => setPending(null)}>Cancel</button>
          </div>
        </div>
      )}
      {notice && (
        <div style={{ marginBottom: 10, fontFamily: 'var(--mono)', fontSize: 11, color: notice.ok ? '#4ade80' : '#f87171' }}>
          {notice.ok ? '✓ ' : '⚠ '}{notice.text}
        </div>
      )}

      <div style={{ paddingBottom: 12 }}>
        <div style={label}>CDN</div>
        {cdn.loading ? <span style={{ color: 'var(--text3)' }}>Checking…</span>
          : cdn.error ? <span style={{ color: '#f87171', fontSize: 11 }}>⚠ {cdn.error}</span>
          : (
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <span style={{ fontFamily: 'var(--mono)' }}>{cdn.enabled ? 'On' : 'Off'}</span>
              <button data-write style={btn} disabled={busy}
                onClick={() => preview('set_my_cdn', { enabled: !cdn.enabled }, cdn.enabled ? 'Turn the CDN off' : 'Turn the CDN on')}>
                {cdn.enabled ? 'Turn off' : 'Turn on'}
              </button>
              {cdn.enabled && (
                <button data-write style={btn} disabled={busy}
                  onClick={() => preview('purge_my_cdn_cache', {}, 'Purge the CDN cache')}>
                  Purge cache
                </button>
              )}
            </div>
          )}
      </div>

      <div style={section}>
        <div style={{ ...label, display: 'flex', justifyContent: 'space-between' }}>
          <span>Restore points</span>
          <button style={{ ...btn, padding: '1px 8px' }} onClick={loadBackups} disabled={backups.loading}>↻</button>
        </div>
        {backups.loading ? <div style={{ color: 'var(--text3)' }}>Loading restore points…</div>
          : backups.error ? <div style={{ color: '#f87171', fontSize: 11 }}>⚠ {backups.error}</div>
          : backups.list.length === 0 ? <div style={{ color: 'var(--text3)' }}>No restore points yet.</div>
          : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {backups.list.map(p => (
                <div key={p.backupId} style={{ display: 'grid', gridTemplateColumns: 'minmax(140px,2fr) 80px 80px auto', gap: 8, alignItems: 'center' }}>
                  <span style={{ fontFamily: 'var(--mono)' }}>{formatPointDate(p.createdAt)}</span>
                  <span style={{ fontFamily: 'var(--mono)', color: 'var(--text3)' }}>{p.kind || ''}</span>
                  <span style={{ fontFamily: 'var(--mono)', color: 'var(--text3)' }}>{formatBytes(p.sizeBytes)}</span>
                  <span style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <button data-write style={btn} disabled={busy || !p.backupId}
                      onClick={() => preview('restore_my_site_backup', { backupId: p.backupId }, `Restore ${domain} to ${formatPointDate(p.createdAt)}`)}>
                      Restore
                    </button>
                  </span>
                </div>
              ))}
            </div>
          )}
      </div>
    </div>
  );
}
